import { canvasRevision, isAnimationFrame } from './pixel';
import { imageQuad } from './webglGeometry';
import { gpuPrepare } from './gpuPrepare';
import { GLOBE } from './globe';
import { GradientTextures } from './gradientTextures';
import { createGpuRenderer, gpuKind, SHARP_WGSL, uploadRegions, type GpuKind, type GpuRenderer } from './gpuBackend';
// Pixi's browser extensions (accessibility, DOM, events), loaded with the game's code. Left to
// `init`, they come from a dynamic import, the one download of the GPU step: on the dev server a
// stale copy of Vite's prebundled pixi in the browser's cache could ask for a chunk that is gone.
import 'pixi.js/browser';
/** WebGL scene drawing. Canvas remains an asset authoring tool, not the world compositor. */
import {
  CanvasSource,
  Color,
  Container,
  FillGradient,
  FillPattern,
  GpuProgram,
  Graphics,
  ImageSource,
  Matrix,
  Mesh,
  MeshGeometry,
  Rectangle,
  RenderTexture,
  Shader,
  Sprite,
  Texture,
  UniformGroup,
} from 'pixi.js';

/**
 * A colour string with any exponent-notation number written out (`rgba(…,3.05e-16)` → `0.0000`).
 * Template-built alphas can come out like that, which Pixi's colour parser rejects (it throws and
 * stops the frame loop), while a canvas accepts them.
 */
function plainColor(value: string): string {
  // Only inside a function's arguments: a hex colour like #200e38 is not a number.
  return value.includes('(') && /\de[-+]?\d/i.test(value)
    ? value.replace(/\(([^)]*)\)/g, (args: string) =>
        args.replace(/-?\d*\.?\d+e[-+]?\d+/gi, (n) => Number(n).toFixed(4)),
      )
    : value;
}

type Image = HTMLCanvasElement | OffscreenCanvas | ImageBitmap | HTMLImageElement;
export type SceneImage = { img: HTMLCanvasElement; x: number; y: number; width: number; height: number };
type Command = { method: string; args: number[]; matrix: Matrix };
type Shape = {
  path: Command[];
  /** Paths only grow after a paint (fill, then more segments, then stroke): the first `length` commands. */
  length: number;
  stroke: boolean;
  width: number;
  cap: CanvasLineCap;
  join: CanvasLineJoin;
};

/** Whether `path` is the same commands, arguments and transforms as the first `length` of `old`. */
function samePath(old: Command[], length: number, path: Command[]): boolean {
  if (path.length !== length) return false;
  for (let i = 0; i < length; i++) {
    const a = old[i]!,
      b = path[i]!;
    if (a === b) continue;
    if (a.method !== b.method || a.args.length !== b.args.length) return false;
    for (let k = 0; k < a.args.length; k++) if (a.args[k] !== b.args[k]) return false;
    const m = a.matrix,
      n = b.matrix;
    if (m.a !== n.a || m.b !== n.b || m.c !== n.c || m.d !== n.d || m.tx !== n.tx || m.ty !== n.ty) return false;
  }
  return true;
}
type Page = {
  animation: boolean;
  canvas: HTMLCanvasElement;
  texture: Texture;
  x: number;
  y: number;
  row: number;
  /** On the GPU yet; until then the first upload sends the whole page, pending images and all. */
  live: boolean;
  /** Images drawn into the page since the last upload, sent on their own (not the 16 MiB page). */
  pending: { image: Image; x: number; y: number }[];
  used: number;
  textures: Set<Texture>;
};
type Entry = {
  texture: Texture;
  page?: Page;
  used: number;
  revision?: number;
  dynamic?: boolean;
  /** The downscaled copy a standalone texture is uploaded from (see `TEXTURE_SCALE`). */
  shrunk?: HTMLCanvasElement;
};
const SIZE = 2048;
/** Textures go to the GPU at this fraction of their drawn resolution, then stretch back to size. */
const TEXTURE_SCALE = 1;
const shrunkSize = (n: number): number => Math.max(1, Math.round(n * TEXTURE_SCALE));
/** `image` redrawn at `TEXTURE_SCALE`, hard pixels kept (no smoothing). */
function shrink(image: Image, into?: HTMLCanvasElement): HTMLCanvasElement {
  const out = into ?? document.createElement('canvas');
  const w = shrunkSize(image.width),
    h = shrunkSize(image.height);
  if (out.width !== w || out.height !== h) {
    out.width = w;
    out.height = h;
  }
  const g = out.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  g.clearRect(0, 0, w, h);
  g.drawImage(image, 0, 0, w, h);
  return out;
}
/** Idle textures let go per frame (`TextureBank.trim`). */
const TRIM_PER_FRAME = 4;
/** Idle sprite batches let go per frame (`WebGLCanvas.finish`). */
const BATCH_DROPS_PER_FRAME = 8;
/** Past this many batches, those idle for 2 frames go too (still a few per frame). */
const BATCH_SOFT_CAP = 512;
const MAX_PAGES = 8; // 128 MiB of RGBA sprite atlases.

/** Packs immutable sprites with a one-pixel gutter; large/dynamic images get their own texture. */
class TextureBank {
  private entries = new WeakMap<Image, Entry>();
  private pages: Page[] = [];
  private standalone = new Map<Image, Entry>();
  private frame = 0;
  readonly targets = new WeakMap<Image, Texture>();
  begin(): void {
    this.frame++;
  }
  get(image: Image, isolated = false): Texture {
    const target = this.targets.get(image);
    if (target) return target;
    const old = this.entries.get(image);
    if (old && !old.texture.destroyed && (!isolated || !old.page)) {
      old.used = this.frame;
      if (old.page) old.page.used = this.frame;
      const revision = canvasRevision(image);
      if (old.revision !== revision) {
        if (old.page) {
          const g = old.page.canvas.getContext('2d')!;
          const f = old.texture.frame;
          const small = shrink(image);
          g.clearRect(f.x, f.y, f.width, f.height);
          g.drawImage(small, f.x, f.y);
          old.page.pending.push({ image: small, x: f.x, y: f.y });
        } else {
          if (old.shrunk) shrink(image, old.shrunk);
          old.texture.source.update();
        }
        old.revision = revision;
      }
      if (old.dynamic) {
        if (old.shrunk) shrink(image, old.shrunk);
        old.texture.source.update();
      }
      return old.texture;
    }
    if (image.width <= 0 || image.height <= 0) return Texture.EMPTY;
    // A ground chunk painted by the workers (terrain.ts) arrives as a bitmap: a texture of its own, straight from it.
    // Copied into an atlas page (or a canvas first) it cost 36–61 ms on its first frame on a big map (lane C0-P1).
    if (!isolated && typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {
      const source = new ImageSource({ resource: image, scaleMode: 'nearest', autoGenerateMipmaps: false });
      const texture = new Texture({ source });
      const entry: Entry = { texture, used: this.frame };
      this.entries.set(image, entry);
      this.standalone.set(image, entry);
      return texture;
    }
    // Patterns tile at the texture's own size, so they keep their full resolution. At full scale a canvas goes into its
    // page, or becomes its own texture, as it is: a copy would be the same picture, and a copy per texture (thousands of
    // canvases on a big map) made each major GC stall the frame for 30–40 ms in Blink's share of it (lane C0-P1).
    const small = isolated ? undefined : TEXTURE_SCALE === 1 && image instanceof HTMLCanvasElement ? image : shrink(image);
    const { width, height } = small ?? image;
    const orig = new Rectangle(0, 0, image.width, image.height);
    let page: Page | undefined;
    if (!isolated && width <= 512 && height <= 512) {
      const animation = isAnimationFrame(image);
      page = this.pages.find((p) => p.animation === animation && this.fits(p, width + 2, height + 2));
      // Reserve six pages for movement/action frames so large scenery cannot exhaust
      // the sprite budget before a crowd appears. Both pools share the 128 MiB ceiling.
      const limit = animation ? MAX_PAGES - 2 : 2;
      if (!page && this.pages.filter((p) => p.animation === animation).length < limit) {
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = SIZE;
        page = {
          animation,
          canvas,
          texture: new Texture({
            source: new CanvasSource({ resource: canvas, scaleMode: 'nearest', autoGenerateMipmaps: false }),
          }),
          x: 0,
          y: 0,
          row: 0,
          live: false,
          pending: [],
          used: this.frame,
          textures: new Set(),
        };
        this.pages.push(page);
      }
    }
    let texture: Texture;
    if (page) {
      if (page.x + width + 2 > SIZE) {
        page.x = 0;
        page.y += page.row;
        page.row = 0;
      }
      const x = page.x + 1,
        y = page.y + 1;
      page.canvas.getContext('2d')!.drawImage(small!, x, y);
      texture = new Texture({ source: page.texture.source, frame: new Rectangle(x, y, width, height), orig });
      page.textures.add(texture);
      page.x += width + 2;
      page.row = Math.max(page.row, height + 2);
      page.pending.push({ image: small!, x, y });
      page.used = this.frame;
    } else if (small) {
      const source = new CanvasSource({ resource: small, scaleMode: 'nearest', autoGenerateMipmaps: false });
      texture = new Texture({ source, frame: new Rectangle(0, 0, width, height), orig });
    } else {
      texture = Texture.from(image, true);
      texture.source.scaleMode = 'nearest';
    }
    const entry: Entry = {
      texture,
      page,
      used: this.frame,
      revision: canvasRevision(image),
      // A frame taken as it is (above) is its own source: nothing to redraw it into.
      shrunk: page || small === image ? undefined : small,
    };
    this.entries.set(image, entry);
    if (!page) this.standalone.set(image, entry);
    return texture;
  }
  dynamic(image: Image): void {
    const old = this.entries.get(image);
    if (old && !old.page) {
      old.dynamic = true;
      if (old.shrunk) shrink(image, old.shrunk);
      old.texture.source.update();
      return;
    }
    const texture = new Texture({
      source: new CanvasSource({ resource: image as HTMLCanvasElement, scaleMode: 'nearest' }),
    });
    const entry: Entry = { texture, used: this.frame, dynamic: true };
    this.entries.set(image, entry);
    this.standalone.set(image, entry);
  }
  upload(renderer: GpuRenderer): void {
    for (const p of this.pages) {
      if (!p.pending.length) continue;
      const source = p.texture.source;
      if (!p.live) {
        // The page's canvas already holds everything pending.
        renderer.texture.initSource(source);
        p.live = true;
      } else {
        // Only the new sprites: re-sending the whole page for each one stalled the GPU when a
        // crowd or a new stretch of scenery came into view.
        uploadRegions(renderer, source, p.pending);
      }
      p.pending.length = 0;
    }
  }
  /** Idle textures found by the last scan, let go a few per frame (lane C0-P1). */
  private dropQueue: (Page | Image)[] = [];
  trim(): void {
    // Scan idle texture entries once per second, rather than once per displayed frame.
    if (this.frame % 60 === 0) {
      this.dropQueue = [...this.pages.filter((p) => this.frame - p.used > 180), ...[...this.standalone].filter(([, e]) => this.frame - e.used > 180).map(([source]) => source)];
    }
    // Let go of them a few at a time: hundreds at once (a big map's scenery) stalled a frame for 30–160 ms.
    for (let k = 0; k < TRIM_PER_FRAME && this.dropQueue.length; k++) {
      const v = this.dropQueue.pop()!;
      if ('textures' in v && 'pending' in v) {
        const p = v as Page;
        if (this.frame - p.used <= 180 || !this.pages.includes(p)) continue;
        for (const t of p.textures) t.destroy();
        p.texture.destroy(true);
        this.pages.splice(this.pages.indexOf(p), 1);
        continue;
      }
      const e = this.standalone.get(v as Image);
      if (!e || this.frame - e.used <= 180) continue;
      e.texture.destroy(true);
      this.standalone.delete(v as Image);
    }
  }
  private fits(p: Page, w: number, h: number): boolean {
    return p.x + w <= SIZE ? p.y + h <= SIZE : p.y + p.row + h <= SIZE;
  }
  destroy(): void {
    for (const p of this.pages) {
      for (const t of p.textures) t.destroy();
      p.texture.destroy(true);
    }
    for (const e of this.standalone.values()) e.texture.destroy(true);
    this.pages = [];
    this.standalone.clear();
    this.entries = new WeakMap();
  }
}

/** Reuses display objects and their GPU buffers instead of allocating a new scene every frame. */
export class WebGLCanvas {
  readonly stage = new Container();
  readonly context: CanvasRenderingContext2D;
  private readonly state = document.createElement('canvas').getContext('2d')!;
  private sprites: Sprite[] = [];
  private graphics: Graphics[] = [];
  /** What each pooled Graphics holds when it was built from a solid-colour path (see `paint`). */
  private shapes: (Shape | undefined)[] = [];
  private ns = 0;
  private ng = 0;
  private child = 0;
  private frame = 0;
  private readonly batches = new Map<string, { group: Container; sprites: Sprite[]; used: number }>();
  private path: Command[] = [];
  private gradients = new WeakMap<CanvasGradient, FillGradient>();
  private patterns = new WeakMap<CanvasPattern, FillPattern>();
  /** Each pattern's own transform (pattern → user space), as a canvas pattern keeps it. */
  private patternMatrices = new WeakMap<FillPattern, Matrix>();
  /** Gradient ramp textures shared across frames (each pixi FillGradient would paint a canvas of its own). */
  private readonly ramps = new GradientTextures();
  private gradientSpecs = new WeakMap<CanvasGradient, { args: number[]; stops: [number, string][] }>();
  private dashCache = new Map<string, HTMLCanvasElement>();
  private readonly colors = new Map<string, { color: number; alpha: number }>();
  private textCache = new Map<string, { image: HTMLCanvasElement; x: number; y: number }>();
  private crops = new Map<Texture, Map<string, Texture>>();
  constructor(
    readonly canvas: HTMLCanvasElement,
    private readonly bank: TextureBank,
  ) {
    const methods: Record<string, (...args: never[]) => unknown> = {};
    for (const name of [
      'beginPath',
      'closePath',
      'moveTo',
      'lineTo',
      'arc',
      'ellipse',
      'quadraticCurveTo',
      'bezierCurveTo',
      'rect',
      'roundRect',
    ])
      methods[name] = ((...args: number[]) => {
        if (name === 'beginPath') this.path = [];
        else this.path.push({ method: name, args, matrix: this.frozenMatrix() });
      }) as (...args: never[]) => unknown;
    // The transform is followed here as it changes (the canvas still keeps its own, for `getTransform` and the
    // save/restore of styles): reading it back per draw was a DOMMatrix and a pixi Matrix per fillRect/drawImage.
    const s = this.state;
    Object.assign(methods, {
      save: () => {
        s.save();
        this.stack.push(this.ta, this.tb, this.tc, this.td, this.te, this.tf);
      },
      restore: () => {
        s.restore();
        const k = this.stack.length;
        if (k < 6) return;
        this.setOwn(
          this.stack[k - 6]!,
          this.stack[k - 5]!,
          this.stack[k - 4]!,
          this.stack[k - 3]!,
          this.stack[k - 2]!,
          this.stack[k - 1]!,
        );
        this.stack.length = k - 6;
      },
      translate: (x: number, y: number) => {
        s.translate(x, y);
        if (!this.invertible() || !Number.isFinite(x) || !Number.isFinite(y)) return;
        this.setOwn(
          this.ta,
          this.tb,
          this.tc,
          this.td,
          this.te + (x * this.ta + y * this.tc),
          this.tf + (x * this.tb + y * this.td),
        );
      },
      scale: (x: number, y: number) => {
        s.scale(x, y);
        if (!this.invertible() || !Number.isFinite(x) || !Number.isFinite(y)) return;
        this.setOwn(this.ta * x, this.tb * x, this.tc * y, this.td * y, this.te, this.tf);
      },
      // Rare, and their sine/cosine or product are the canvas's own: read back.
      rotate: (angle: number) => {
        s.rotate(angle);
        this.readTransform();
      },
      transform: (a: number, b: number, c: number, d: number, e: number, f: number) => {
        s.transform(a, b, c, d, e, f);
        this.readTransform();
      },
      setTransform: (...args: [number, number, number, number, number, number] | [DOMMatrix2DInit?]) => {
        if (args.length === 6) {
          s.setTransform(...args);
          const [a, b, c, d, e, f] = args;
          if (Number.isFinite(a + b + c + d + e + f)) this.setOwn(a, b, c, d, e, f);
          return;
        }
        s.setTransform(args[0]);
        this.readTransform();
      },
      resetTransform: () => {
        s.resetTransform();
        this.setOwn(1, 0, 0, 1, 0, 0);
      },
      reset: () => {
        s.reset();
        this.resetOwn();
      },
      drawImage: this.drawImage.bind(this),
      fill: () => this.paint(false),
      stroke: () => this.paint(true),
      fillRect: (x: number, y: number, w: number, h: number) => this.rect(x, y, w, h, false),
      strokeRect: (x: number, y: number, w: number, h: number) => this.rect(x, y, w, h, true),
      fillText: (s: string, x: number, y: number) => this.text(s, x, y, false),
      outlinedText: (s: string, x: number, y: number) => this.text(s, x, y, false, true),
      strokeText: (s: string, x: number, y: number) => this.text(s, x, y, true),
      createLinearGradient: (x0: number, y0: number, x1: number, y1: number) =>
        this.gradient([x0, y0, x1, y1]),
      createRadialGradient: (x0: number, y0: number, r0: number, x1: number, y1: number, r1: number) =>
        this.gradient([x0, y0, r0, x1, y1, r1]),
      createPattern: (image: Image, repetition: string) => {
        const pattern = this.state.createPattern(image, repetition)!;
        const fill = new FillPattern(this.bank.get(image, true), 'repeat');
        const original = pattern.setTransform.bind(pattern);
        pattern.setTransform = (matrix?: DOMMatrix2DInit) => {
          original(matrix);
          const m = new DOMMatrix(
            matrix
              ? [matrix.a ?? 1, matrix.b ?? 0, matrix.c ?? 0, matrix.d ?? 1, matrix.e ?? 0, matrix.f ?? 0]
              : undefined,
          );
          const own = new Matrix(m.a, m.b, m.c, m.d, m.e, m.f);
          this.patternMatrices.set(fill, own);
          fill.setTransform(own);
        };
        this.patterns.set(pattern, fill);
        return pattern;
      },
      clearRect: () => {
        throw new Error('WebGL canvas clears at beginFrame, not clearRect');
      },
    });
    const bound = new Map<PropertyKey, unknown>();
    this.context = new Proxy(this.state, {
      get: (target, key) => {
        if (key === 'canvas') return canvas;
        if (typeof key === 'string' && methods[key]) return methods[key];
        const value = Reflect.get(target, key, target) as unknown;
        if (typeof value !== 'function') return value;
        if (!bound.has(key)) bound.set(key, value.bind(target));
        return bound.get(key);
      },
      set: (target, key, value) => Reflect.set(target, key, value, target),
    });
  }
  begin(): void {
    this.frame++;
    this.child = 0;
    this.ns = this.ng = 0;
    this.path = [];
    this.state.reset();
    this.resetOwn();
    for (const [source, crops] of this.crops)
      if (source.destroyed) {
        for (const crop of crops.values()) crop.destroy();
        this.crops.delete(source);
      }
    // Gradients are transient draw descriptions; their ramp textures are shared (gradientTextures.ts).
    this.ramps.begin(this.frame);
    this.gradients = new WeakMap();
    this.patterns = new WeakMap();
  }
  finish(): void {
    // Idle batches go a few per frame, oldest first: destroying hundreds at once (the old once-a-second sweep, or the
    // 1024 cap, as a big map's scenery came and went) stalled a frame for 30–380 ms (lane C0-P1).
    if (this.batches.size > BATCH_SOFT_CAP || this.frame % 60 === 0) {
      let n = 0;
      const idle = this.batches.size > BATCH_SOFT_CAP ? 2 : 180;
      for (const [key, batch] of this.batches) {
        if (n >= BATCH_DROPS_PER_FRAME) break;
        if (this.frame - batch.used <= idle) continue;
        batch.group.destroy({ children: true });
        this.batches.delete(key);
        n++;
      }
    }
    const order = this.order;
    order.length = this.child;
    const children = this.stage.children;
    let same = !this.reordered && children.length === order.length;
    for (let i = 0; same && i < order.length; i++) same = children[i] === order[i];
    this.reordered = false;
    if (same) return;
    // Rebuilt in one pass: moving nodes one by one with addChildAt is an indexOf and a splice
    // per node, quadratic in a crowd's tens of thousands of draws.
    this.stage.removeChildren();
    for (const node of order) this.stage.addChild(node);
  }
  /** This frame's draw order; applied to the stage once, in `finish`. */
  private readonly order: Container[] = [];
  private reordered = false;
  private attach(node: Container): void {
    if (this.order[this.child] !== node) {
      this.order[this.child] = node;
      this.reordered = true;
    }
    this.child++;
  }
  /** Draws a ready-made node (a mesh with its own shader) at this point in the frame. */
  node(node: Container): void {
    this.attach(node);
  }
  /** C0-ROTATE: the GPU texture of an image, as drawImage would use it (render/turned/groundGpu.ts). */
  textureOf(image: Image): Texture {
    return this.bank.get(image);
  }
  batch(key: string, images: readonly SceneImage[], ox: number, oy: number): void {
    let batch = this.batches.get(key);
    if (!batch) {
      batch = { group: new Container(), sprites: [], used: this.frame };
      this.batches.set(key, batch);
    }
    batch.used = this.frame;
    const m = this.matrix();
    m.translate(ox * m.a + oy * m.c, ox * m.b + oy * m.d);
    if (m.b === 0 && m.c === 0) {
      batch.group.position.set(m.tx, m.ty);
      batch.group.scale.set(m.a, m.d);
    } else batch.group.setFromMatrix(m);
    for (let i = 0; i < images.length; i++) {
      const item = images[i]!;
      let sprite = batch.sprites[i];
      if (!sprite) {
        sprite = new Sprite();
        batch.sprites.push(sprite);
        batch.group.addChild(sprite);
      }
      sprite.texture = this.bank.get(item.img);
      sprite.position.set(item.x, item.y);
      const scaleX = item.width / sprite.texture.orig.width;
      const scaleY = item.height / sprite.texture.orig.height;
      sprite.scale.set(scaleX, scaleY);
    }
    // A batch kept by its first prop (renderer.ts) may hold more sprites than this run: those go (a hidden sprite would
    // keep a texture the bank may let go of meanwhile).
    while (batch.sprites.length > images.length) batch.sprites.pop()!.destroy();
    this.attach(batch.group);
  }
  /** The current transform, as the canvas keeps it (see the transform methods in the constructor). */
  private ta = 1;
  private tb = 0;
  private tc = 0;
  private td = 1;
  private te = 0;
  private tf = 0;
  /** Saved transforms, six numbers each. */
  private readonly stack: number[] = [];
  /** The current transform as a Matrix shared by path commands until it changes (never written to). */
  private frozen: Matrix | null = null;
  /** Scratch matrices for one draw (`matrix`, `part`): their values are copied out before the next draw. */
  private readonly scratch = new Matrix();
  private readonly scratchPart = new Matrix();
  private setOwn(a: number, b: number, c: number, d: number, e: number, f: number): void {
    this.ta = a;
    this.tb = b;
    this.tc = c;
    this.td = d;
    this.te = e;
    this.tf = f;
    this.frozen = null;
  }
  private resetOwn(): void {
    this.setOwn(1, 0, 0, 1, 0, 0);
    this.stack.length = 0;
  }
  private readTransform(): void {
    const m = this.state.getTransform();
    this.setOwn(m.a, m.b, m.c, m.d, m.e, m.f);
  }
  /** As the canvas: translate/scale/rotate/transform do nothing while the transform cannot be inverted. */
  private invertible(): boolean {
    const det = this.ta * this.td - this.tb * this.tc;
    return det !== 0 && Number.isFinite(det);
  }
  /** The current transform in a scratch Matrix, valid until the next draw. */
  private matrix(): Matrix {
    return this.scratch.set(this.ta, this.tb, this.tc, this.td, this.te, this.tf);
  }
  /** `matrix()` times a part (a, 0, 0, d, tx, ty), as `Matrix.append` works it out. */
  private matrixTimes(a: number, d: number, tx: number, ty: number): Matrix {
    return this.matrix().append(this.scratchPart.set(a, 0, 0, d, tx, ty));
  }
  /** The current transform as a Matrix kept by a path command. */
  private frozenMatrix(): Matrix {
    return (this.frozen ??= new Matrix(this.ta, this.tb, this.tc, this.td, this.te, this.tf));
  }
  private blend(): 'normal' | 'add' | 'multiply' | 'screen' | 'erase' {
    switch (this.state.globalCompositeOperation) {
      case 'lighter':
        return 'add';
      case 'multiply':
        return 'multiply';
      case 'screen':
        return 'screen';
      case 'destination-out':
        return 'erase';
      default:
        return 'normal';
    }
  }
  private sprite(texture: Texture, matrix: Matrix, alpha = this.state.globalAlpha, tint = 0xffffff): Sprite {
    const sprite = this.sprites[this.ns] ?? (this.sprites[this.ns] = new Sprite());
    this.ns++;
    // Pooled sprites mostly keep last frame's values: skip setters that would re-normalise a
    // colour or flag a render-group change for nothing.
    if (sprite.texture !== texture) sprite.texture = texture;
    if (sprite.tint !== tint) sprite.tint = tint;
    if (sprite.alpha !== alpha) sprite.alpha = alpha;
    const blend = this.blend();
    if (sprite.blendMode !== blend) sprite.blendMode = blend;
    if (matrix.b === 0 && matrix.c === 0) {
      sprite.position.set(matrix.tx, matrix.ty);
      sprite.scale.set(matrix.a, matrix.d);
      sprite.rotation = 0;
      sprite.skew.set(0, 0);
    } else sprite.setFromMatrix(matrix);
    this.attach(sprite);
    return sprite;
  }
  imageAt(
    image: Image,
    x: number,
    y: number,
    dx: number,
    dy: number,
    width: number,
    height: number,
    facing: number,
    rotation: number,
  ): void {
    const m = this.matrix();
    const c = Math.cos(rotation),
      s = Math.sin(rotation);
    m.append(this.scratchPart.set(c * facing, s, -s * facing, c, x, y));
    m.append(this.scratchPart.set(width / image.width, 0, 0, height / image.height, dx, dy));
    this.sprite(this.bank.get(image), m);
  }
  private drawImage(image: Image, ...args: number[]): void {
    const quad = imageQuad(image.width, image.height, args);
    if (!quad) return;
    const { sx, sy, sw, sh, dx, dy, dw, dh } = quad;
    let texture = this.bank.get(image);
    if (sx || sy || sw !== image.width || sh !== image.height) {
      let crops = this.crops.get(texture);
      if (!crops) this.crops.set(texture, (crops = new Map()));
      const key = `${sx},${sy},${sw},${sh}`;
      let crop = crops.get(key);
      if (!crop) {
        // Source coordinates are in the image's pixels; the texture may hold it downscaled.
        const kx = texture.frame.width / texture.orig.width,
          ky = texture.frame.height / texture.orig.height;
        const x0 = Math.round(sx * kx),
          y0 = Math.round(sy * ky);
        crop = new Texture({
          source: texture.source,
          frame: new Rectangle(
            texture.frame.x + x0,
            texture.frame.y + y0,
            Math.max(1, Math.round((sx + sw) * kx) - x0),
            Math.max(1, Math.round((sy + sh) * ky) - y0),
          ),
          orig: new Rectangle(0, 0, sw, sh),
        });
        crops.set(key, crop);
      }
      texture = crop;
    }
    this.sprite(texture, this.matrixTimes(dw / sw, dh / sh, dx, dy));
  }
  private graphic(clear = true): Graphics {
    const g = this.graphics[this.ng] ?? (this.graphics[this.ng] = new Graphics());
    this.ng++;
    if (clear) g.clear();
    g.alpha = this.state.globalAlpha;
    if (g.tint !== 0xffffff) g.tint = 0xffffff;
    g.blendMode = this.blend();
    this.attach(g);
    return g;
  }
  private color(value: string): { color: number; alpha: number } {
    let result = this.colors.get(value);
    if (!result) {
      const c = new Color(plainColor(value));
      result = { color: c.toNumber(), alpha: c.alpha };
      if (this.colors.size >= 1024) this.colors.clear();
      this.colors.set(value, result);
    }
    return result;
  }
  /**
   * A pattern laid in user space, as on a canvas: the path's points reach the GPU already through
   * the transform, so the pattern goes through it too (else it sat at device pixels, sliding with
   * the camera and jumping when the world picture is resized).
   */
  private placed<T>(fill: T): T {
    if (!(fill instanceof FillPattern)) return fill;
    const ctm = this.path[this.path.length - 1]?.matrix ?? this.matrix();
    fill.setTransform(ctm.clone().append(this.patternMatrices.get(fill) ?? new Matrix()));
    return fill;
  }
  private style(value: string | CanvasGradient | CanvasPattern) {
    if (typeof value === 'string') {
      return this.color(value);
    }
    let fill: FillGradient | undefined = this.gradients.get(value as CanvasGradient);
    if (!fill) {
      const spec = this.gradientSpecs.get(value as CanvasGradient);
      if (spec) {
        fill = this.ramps.fill(spec.args, spec.stops);
        this.gradients.set(value as CanvasGradient, fill);
      }
    }
    return fill ?? this.patterns.get(value as CanvasPattern) ?? { color: 0xffffff };
  }
  private rect(x: number, y: number, w: number, h: number, stroke: boolean): void {
    if (!w || !h) return;
    if (!stroke && typeof this.state.fillStyle === 'string') {
      const color = this.color(this.state.fillStyle);
      this.sprite(
        Texture.WHITE,
        this.matrixTimes(w, h, x, y),
        this.state.globalAlpha * color.alpha,
        color.color,
      );
      return;
    }
    const old = this.path;
    this.path = [{ method: 'rect', args: [x, y, w, h], matrix: this.frozenMatrix() }];
    this.paint(stroke);
    this.path = old;
  }
  private paint(stroke: boolean): void {
    const slot = this.ng;
    const s = this.state;
    const style = stroke ? s.strokeStyle : s.fillStyle;
    if (typeof style === 'string' && !(stroke && s.getLineDash().length)) {
      // A solid colour: the shape is tessellated in white and coloured by the Graphics' tint and alpha (the same
      // product pixi bakes into its vertices), so a pooled Graphics that drew the very same path last frame keeps
      // its geometry: no clear and rebuild (the pixi path/tessellation garbage) when only a colour pulses.
      const c = this.color(style);
      const prev = this.shapes[slot];
      const same =
        !!prev &&
        prev.stroke === stroke &&
        (!stroke || (prev.width === s.lineWidth && prev.cap === s.lineCap && prev.join === s.lineJoin)) &&
        samePath(prev.path, prev.length, this.path);
      const g = this.graphic(!same);
      if (g.tint !== c.color) g.tint = c.color;
      g.alpha = s.globalAlpha * c.alpha;
      if (same) return;
      this.trace(g);
      if (stroke)
        g.stroke({ color: 0xffffff, alpha: 1, width: s.lineWidth, cap: s.lineCap, join: s.lineJoin });
      else g.fill({ color: 0xffffff, alpha: 1 });
      this.shapes[slot] = {
        path: this.path,
        length: this.path.length,
        stroke,
        width: s.lineWidth,
        cap: s.lineCap,
        join: s.lineJoin,
      };
      return;
    }
    this.shapes[slot] = undefined;
    const g = this.graphic();
    this.trace(g);
    if (stroke) {
      const fill = this.style(this.state.strokeStyle);
      g.stroke({
        ...(fill instanceof FillGradient || fill instanceof FillPattern ? { fill } : fill),
        width: this.state.lineWidth,
        cap: this.state.lineCap,
        join: this.state.lineJoin,
      });
    } else g.fill(this.placed(this.style(this.state.fillStyle)));
    if (stroke && this.state.getLineDash().length) this.dashed(g);
  }
  /** The current path into a Graphics. */
  private trace(g: Graphics): void {
    for (const command of this.path) {
      const a = command.args;
      g.context.setTransform(command.matrix);
      switch (command.method) {
        case 'moveTo':
          g.moveTo(a[0]!, a[1]!);
          break;
        case 'lineTo':
          g.lineTo(a[0]!, a[1]!);
          break;
        case 'closePath':
          g.closePath();
          break;
        case 'rect':
          g.rect(a[0]!, a[1]!, a[2]!, a[3]!);
          break;
        case 'roundRect':
          g.roundRect(a[0]!, a[1]!, a[2]!, a[3]!, a[4] ?? 0);
          break;
        case 'quadraticCurveTo':
          g.quadraticCurveTo(a[0]!, a[1]!, a[2]!, a[3]!);
          break;
        case 'bezierCurveTo':
          g.bezierCurveTo(a[0]!, a[1]!, a[2]!, a[3]!, a[4]!, a[5]!);
          break;
        case 'arc':
          g.arc(a[0]!, a[1]!, a[2]!, a[3]!, a[4]!, !!a[5]);
          break;
        case 'ellipse': {
          const [x, y, rx, ry, rot, start, end] = a as [
            number,
            number,
            number,
            number,
            number,
            number,
            number,
          ];
          const delta = end - start;
          for (let i = 0; i <= 48; i++) {
            const t = start + (delta * i) / 48;
            const px = x + Math.cos(t) * rx * Math.cos(rot) - Math.sin(t) * ry * Math.sin(rot);
            const py = y + Math.cos(t) * rx * Math.sin(rot) + Math.sin(t) * ry * Math.cos(rot);
            if (i === 0) g.moveTo(px, py);
            else g.lineTo(px, py);
          }
          if (Math.abs(delta) >= Math.PI * 2) g.closePath();
          break;
        }
      }
    }
  }
  private dashed(graphic: Graphics): void {
    // Canvas is used only to author the small dashed shape; its compositing stays on WebGL.
    const b = graphic.getLocalBounds();
    const x = Math.floor(b.x - 2),
      y = Math.floor(b.y - 2);
    const width = Math.ceil(b.width + 4),
      height = Math.ceil(b.height + 4);
    if (!width || !height || width > 4096 || height > 4096) return;
    const commands = this.path.map((c) => ({
      ...c,
      matrix: new Matrix(c.matrix.a, c.matrix.b, c.matrix.c, c.matrix.d, c.matrix.tx - x, c.matrix.ty - y),
    }));
    const key = JSON.stringify([
      commands,
      this.state.strokeStyle,
      this.state.lineWidth,
      this.state.getLineDash(),
      this.state.lineDashOffset,
      width,
      height,
    ]);
    let image = this.dashCache.get(key);
    if (!image) {
      image = document.createElement('canvas');
      image.width = width;
      image.height = height;
      const g = image.getContext('2d')!;
      g.strokeStyle = this.state.strokeStyle;
      g.lineWidth = this.state.lineWidth;
      g.lineCap = this.state.lineCap;
      g.lineJoin = this.state.lineJoin;
      g.setLineDash(this.state.getLineDash());
      g.lineDashOffset = this.state.lineDashOffset;
      g.beginPath();
      for (const c of commands) {
        const m = c.matrix;
        g.setTransform(m.a, m.b, m.c, m.d, m.tx, m.ty);
        const method = Reflect.get(g, c.method, g) as (...a: number[]) => void;
        method.apply(g, c.args);
      }
      g.stroke();
      this.dashCache.set(key, image);
      if (this.dashCache.size > 128) this.dashCache.delete(this.dashCache.keys().next().value!);
    }
    this.stage.removeChild(graphic);
    const m = new Matrix(1, 0, 0, 1, x, y);
    this.sprite(this.bank.get(image), m);
  }
  private gradient(a: number[]): CanvasGradient {
    const native =
      a.length === 6
        ? this.state.createRadialGradient(a[0]!, a[1]!, a[2]!, a[3]!, a[4]!, a[5]!)
        : this.state.createLinearGradient(a[0]!, a[1]!, a[2]!, a[3]!);
    const spec = { args: a, stops: [] as [number, string][] };
    this.gradientSpecs.set(native, spec);
    const add = native.addColorStop.bind(native);
    native.addColorStop = (offset, raw) => {
      const color = plainColor(raw);
      add(offset, color);
      spec.stops.push([offset, color]);
    };
    // The pixi fill is made at first use (style), from a ramp shared across frames.
    return native;
  }
  private text(text: string, x: number, y: number, stroke: boolean, outlined = false): void {
    if (!text) return;
    const s = this.state;
    const style = stroke ? s.strokeStyle : s.fillStyle;
    const key = JSON.stringify([
      text,
      s.font,
      s.textAlign,
      s.textBaseline,
      style,
      stroke,
      stroke || outlined ? s.lineWidth : 0,
      outlined ? s.strokeStyle : null,
      outlined ? s.lineJoin : null,
    ]);
    let cached = typeof style === 'string' ? this.textCache.get(key) : undefined;
    if (!cached) {
      const metrics = s.measureText(text);
      const padding = Math.ceil(s.lineWidth + 4);
      const left = Math.ceil(metrics.actualBoundingBoxLeft + padding);
      const top = Math.ceil(metrics.actualBoundingBoxAscent + padding);
      const image = document.createElement('canvas');
      image.width = Math.max(
        1,
        Math.ceil(metrics.actualBoundingBoxLeft + metrics.actualBoundingBoxRight + padding * 2),
      );
      image.height = Math.max(
        1,
        Math.ceil(metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent + padding * 2),
      );
      const g = image.getContext('2d')!;
      g.font = s.font;
      g.textAlign = s.textAlign;
      g.textBaseline = s.textBaseline;
      g.fillStyle = s.fillStyle;
      g.strokeStyle = s.strokeStyle;
      g.lineWidth = s.lineWidth;
      g.lineJoin = s.lineJoin;
      const spec = typeof style !== 'string' ? this.gradientSpecs.get(style as CanvasGradient) : undefined;
      if (spec) {
        // Gradient coordinates belong to the text's original coordinate system.
        g.translate(left - x, top - y);
        const a = spec.args;
        const ramp =
          a.length === 6
            ? g.createRadialGradient(a[0]!, a[1]!, a[2]!, a[3]!, a[4]!, a[5]!)
            : g.createLinearGradient(a[0]!, a[1]!, a[2]!, a[3]!);
        for (const [offset, color] of spec.stops) ramp.addColorStop(offset, color);
        if (stroke) {
          g.strokeStyle = ramp;
          g.strokeText(text, x, y);
        } else {
          g.fillStyle = ramp;
          g.fillText(text, x, y);
        }
      } else if (stroke) g.strokeText(text, left, top);
      else {
        if (outlined) g.strokeText(text, left, top);
        g.fillText(text, left, top);
      }
      cached = { image, x: -left, y: -top };
      if (typeof style === 'string') {
        this.textCache.set(key, cached);
        if (this.textCache.size > 2048) this.textCache.delete(this.textCache.keys().next().value!);
      }
    }
    this.drawImage(cached.image, x + cached.x, y + cached.y);
  }
  clearText(): void {
    this.textCache.clear();
  }
  destroy(): void {
    this.stage.removeChildren();
    for (const batch of this.batches.values()) batch.group.destroy({ children: true });
    this.batches.clear();
    for (const s of this.sprites) s.destroy();
    for (const g of this.graphics) g.destroy();
    for (const map of this.crops.values()) for (const t of map.values()) t.destroy();
    this.ramps.destroy();
    this.textCache.clear();
    this.dashCache.clear();
    this.crops.clear();
    this.stage.destroy();
  }
}

/**
 * A render target that is resized as the window or zoom changes. Sprites only follow a texture's
 * size changes when it is `dynamic`; otherwise they keep the old bounds, and the world was drawn
 * magnified off-centre after every zoom step.
 */
function dynamicTarget(width: number, height: number): RenderTexture {
  const target = RenderTexture.create({ width, height, scaleMode: 'nearest' });
  target.dynamic = true;
  return target;
}

const SHARP_VERTEX = `
in vec2 aPosition;
in vec2 aUV;
out vec2 vUV;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUV = aUV;
}`;

/**
 * Sharp bilinear: each texel a hard block at any fractional scale, blended with its neighbour only
 * across the one screen pixel its edge falls in, so no column comes out a pixel wider than the
 * rest and nothing shimmers while the zoom moves. The bilinear is done by hand from four texel
 * centres, so the target keeps its nearest sampler. `uScale`: screen pixels per texel.
 */
const SHARP_FRAGMENT = `
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec2 uSize;
uniform vec2 uScale;
uniform vec2 uOrigin;
uniform vec2 uFlip;
uniform vec4 uGlobe;
uniform vec4 uSkyLow;
uniform vec4 uSkyTop;
void main() {
  // From the pixel centre (exact on both backends), not the interpolated UV: interpolation
  // differs by an ulp between WebGL and WebGPU and moved the seam blend by one level.
  vec2 frag = vec2(gl_FragCoord.x, uFlip.x + uFlip.y * gl_FragCoord.y);
  vec2 p = (frag - uOrigin) / uScale;
  // Globe View (globe.ts): the texel this bent pixel shows, or the sky over the horizon.
  if (uGlobe.w > 0.5) {
    float X = p.x - uGlobe.x;
    float Y = p.y - uGlobe.y;
    float K = uGlobe.z;
    float disc = 1.0 - 16.0 * K * (K * X * X - Y);
    if (disc < 0.0) {
      float up = 1.0 / (16.0 * K);
      float s = clamp((-Y - up) / max(uGlobe.y - up, 1.0), 0.0, 1.0);
      finalColor = vec4(mix(uSkyLow.rgb, uSkyTop.rgb, s), 1.0);
      return;
    }
    p.y = uGlobe.y + (sqrt(disc) - 1.0) / (8.0 * K);
  }
  // C0-ROTATE: hard texels (nearest). The seam blend over one device pixel, at 1.25 or 2.5 device
  // pixels a texel, beat into a faint dotted grid over the whole picture; nearest keeps every art
  // pixel one solid colour (on a 2x screen each is exactly 5 device pixels at the default zoom).
  p = floor(p) + 0.5;
  vec2 i = floor(p - 0.5);
  vec2 f = p - 0.5 - i;
  vec2 t = 1.0 / uSize;
  vec4 a = texture(uTexture, (i + vec2(0.5, 0.5)) * t);
  vec4 b = texture(uTexture, (i + vec2(1.5, 0.5)) * t);
  vec4 c = texture(uTexture, (i + vec2(0.5, 1.5)) * t);
  vec4 d = texture(uTexture, (i + vec2(1.5, 1.5)) * t);
  finalColor = mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}`;

/** A unit quad that draws `target` through the sharp-bilinear shader; place it by its transform. */
function sharpBlit(target: RenderTexture): { mesh: Mesh<MeshGeometry, Shader>; uniforms: UniformGroup } {
  const quad = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
  const geometry = new MeshGeometry({
    positions: quad,
    uvs: quad.slice(),
    indices: new Uint32Array([0, 1, 2, 0, 2, 3]),
  });
  const uniforms = new UniformGroup({
    uSize: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
    uScale: { value: new Float32Array([1, 1]), type: 'vec2<f32>' },
    uOrigin: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
    uFlip: { value: new Float32Array([0, 1]), type: 'vec2<f32>' },
    uGlobe: { value: new Float32Array([0, 0, 0, 0]), type: 'vec4<f32>' },
    uSkyLow: { value: new Float32Array([0, 0, 0, 1]), type: 'vec4<f32>' },
    uSkyTop: { value: new Float32Array([0, 0, 0, 1]), type: 'vec4<f32>' },
  });
  const shader = Shader.from({
    gl: { vertex: SHARP_VERTEX, fragment: SHARP_FRAGMENT, name: 'sharp-blit' },
    gpu: GpuProgram.from({
      vertex: { source: SHARP_WGSL, entryPoint: 'mainVertex' },
      fragment: { source: SHARP_WGSL, entryPoint: 'mainFragment' },
      name: 'sharp-blit',
    }),
    resources: { uTexture: target.source, uSampler: target.source.style, blit: uniforms },
  });
  return { mesh: new Mesh<MeshGeometry, Shader>({ geometry, shader, texture: target }), uniforms };
}

export class WebGLDevice {
  private readonly bank = new TextureBank();
  readonly world: WebGLCanvas;
  readonly screen: WebGLCanvas;
  private readonly prepared = new Set<Image>();
  private prepareTimer: ReturnType<typeof setTimeout> | undefined;
  private disposed = false;
  prepare(image: Image): void {
    if (this.disposed || this.prepared.has(image)) return;
    this.prepared.add(image);
    if (this.prepareTimer !== undefined) return;
    this.prepareTimer = setTimeout(() => this.pumpPrepared(), 16);
  }
  private pumpPrepared(): void {
    this.prepareTimer = undefined;
    if (this.disposed) return;
    if (this.uploadPrepared(2)) this.prepareTimer = setTimeout(() => this.pumpPrepared(), 16);
  }
  /** Uploads images waiting in `prepare` for up to `ms` (the loading screen); returns how many are left. */
  uploadPrepared(ms: number): number {
    if (this.disposed) return 0;
    const end = performance.now() + ms;
    const sources = new Set<Texture['source']>();
    for (const image of this.prepared) {
      this.prepared.delete(image);
      // A bitmap closed meanwhile (a ground chunk let go): nothing to upload.
      if (image.width <= 0) continue;
      sources.add(this.bank.get(image).source);
      if (performance.now() >= end) break;
    }
    this.bank.upload(this.renderer);
    for (const source of sources) this.renderer.texture.initSource(source);
    return this.prepared.size;
  }
  private readonly fontsReady = () => {
    this.world.clearText();
    this.screen.clearText();
  };
  private target = dynamicTarget(8, 8);
  /** The world picture's way onto the screen (`blitWorld`). */
  private readonly blit = sharpBlit(this.target);
  private constructor(
    readonly renderer: GpuRenderer,
    worldCanvas: HTMLCanvasElement,
    view: HTMLCanvasElement,
  ) {
    this.world = new WebGLCanvas(worldCanvas, this.bank);
    this.screen = new WebGLCanvas(view, this.bank);
    this.bank.targets.set(worldCanvas, this.target);
    gpuPrepare.fn = (image) => this.prepare(image as Image);
    gpuPrepare.source = (source) => {
      if (!this.disposed) this.renderer.texture.initSource(source as Texture['source']);
    };
    document.fonts.addEventListener('loadingdone', this.fontsReady);
  }
  static async create(
    view: HTMLCanvasElement,
    worldCanvas: HTMLCanvasElement,
    kind?: GpuKind,
  ): Promise<WebGLDevice> {
    // WebGPU when available, WebGL otherwise; `?gpu=webgl|webgpu` forces one (gpuBackend.ts).
    const renderer = await createGpuRenderer(
      view,
      {
        width: 8,
        height: 8,
        resolution: 1,
        antialias: false,
        backgroundAlpha: 1,
        powerPreference: 'high-performance',
        preserveDrawingBuffer: false,
        // Imported above ('pixi.js/browser'), not downloaded here.
        skipExtensionImports: true,
      },
      kind,
    );
    view.dataset.renderer = gpuKind(renderer);
    // Compile the unbatched-graphics shader behind the loading screen: linking it on the first
    // in-game frame stalled for half a second.
    const warm = new Graphics();
    warm.context.batchMode = 'no-batch';
    warm.rect(0, 0, 2, 2).fill(0xffffff);
    const scratch = RenderTexture.create({ width: 2, height: 2 });
    renderer.render({ container: warm, target: scratch });
    warm.destroy();
    scratch.destroy(true);
    return new WebGLDevice(renderer, worldCanvas, view);
  }
  resize(width: number, height: number, worldWidth: number, worldHeight: number): void {
    this.renderer.resize(width, height);
    this.target.resize(worldWidth, worldHeight);
    this.target.update();
  }
  begin(): void {
    this.bank.begin();
    this.world.begin();
    this.screen.begin();
  }
  drawWorld(): void {
    this.world.finish();
    this.bank.upload(this.renderer);
    this.renderer.render({ container: this.world.stage, target: this.target, clear: true });
  }
  /** Draws the world picture onto the screen at this point, stretched to a box (screen pixels). */
  blitWorld(x: number, y: number, width: number, height: number): void {
    const { mesh, uniforms } = this.blit;
    const source = this.target.source;
    const u = uniforms.uniforms as Record<
      'uSize' | 'uScale' | 'uOrigin' | 'uFlip' | 'uGlobe' | 'uSkyLow' | 'uSkyTop',
      Float32Array
    >;
    u.uSize[0] = source.pixelWidth;
    u.uSize[1] = source.pixelHeight;
    u.uScale[0] = width / source.pixelWidth;
    u.uScale[1] = height / source.pixelHeight;
    u.uOrigin[0] = x;
    u.uOrigin[1] = y;
    // The blit goes onto the screen: WebGL counts pixel rows from the bottom, WebGPU from the top.
    const gl = gpuKind(this.renderer) === 'webgl';
    u.uFlip[0] = gl ? this.renderer.canvas.height : 0;
    u.uFlip[1] = gl ? -1 : 1;
    // Globe View (globe.ts): its centre and drop factor from the low picture's texels to the target's.
    const ts = source.pixelHeight / GLOBE.h;
    u.uGlobe.set([GLOBE.cx * (source.pixelWidth / GLOBE.w), GLOBE.cy * ts, GLOBE.k / ts, GLOBE.k ? 1 : 0]);
    u.uSkyLow.set(GLOBE.skyLow);
    u.uSkyTop.set(GLOBE.skyTop);
    uniforms.update();
    mesh.position.set(x, y);
    mesh.scale.set(width, height);
    this.screen.node(mesh);
  }
  present(): void {
    this.screen.finish();
    this.bank.upload(this.renderer);
    this.renderer.render({ container: this.screen.stage, clear: true });
    this.bank.trim();
  }
  dirty(canvas: HTMLCanvasElement): void {
    this.bank.dynamic(canvas);
  }
  private readonly layers = new Map<
    string,
    { canvas: WebGLCanvas; image: HTMLCanvasElement; target: RenderTexture }
  >();
  /**
   * An offscreen layer composed on the GPU, for a screen-sized mask redrawn every frame (night):
   * draw into the returned context (it starts clear), then `endLayer` gives the image to draw.
   */
  beginLayer(name: string, width: number, height: number): CanvasRenderingContext2D {
    let layer = this.layers.get(name);
    if (!layer) {
      const image = document.createElement('canvas');
      const target = dynamicTarget(width, height);
      layer = { canvas: new WebGLCanvas(image, this.bank), image, target };
      this.bank.targets.set(image, target);
      this.layers.set(name, layer);
    }
    if (layer.image.width !== width || layer.image.height !== height) {
      // The image only carries the size the world draws it at; its pixels live in the target.
      layer.image.width = width;
      layer.image.height = height;
      layer.target.resize(width, height);
      layer.target.update();
    }
    layer.canvas.begin();
    return layer.canvas.context;
  }
  endLayer(name: string): HTMLCanvasElement {
    const layer = this.layers.get(name)!;
    layer.canvas.finish();
    this.bank.upload(this.renderer);
    this.renderer.render({
      container: layer.canvas.stage,
      target: layer.target,
      clear: true,
      clearColor: [0, 0, 0, 0],
    });
    return layer.image;
  }
  snapshot(): HTMLCanvasElement {
    return this.renderer.extract.canvas(this.target) as HTMLCanvasElement;
  }
  /** A copy of the world picture kept on the GPU (the camera turn fades out of it). */
  private still: { image: HTMLCanvasElement; target: RenderTexture } | null = null;
  /**
   * Copies the world picture as it stands (last frame's, until `drawWorld`) on the GPU, with no read
   * back; the returned image draws that copy. The next call overwrites it.
   */
  keepWorld(): HTMLCanvasElement {
    const { pixelWidth: w, pixelHeight: h } = this.target.source;
    if (!this.still) {
      const image = document.createElement('canvas');
      const target = dynamicTarget(w, h);
      this.still = { image, target };
      this.bank.targets.set(image, target);
    }
    const { image, target } = this.still;
    if (image.width !== w || image.height !== h) {
      image.width = w;
      image.height = h;
      target.resize(w, h);
      target.update();
    }
    const copy = new Sprite(this.target);
    this.renderer.render({ container: copy, target, clear: true, clearColor: [0, 0, 0, 0] });
    copy.destroy();
    return image;
  }
  destroy(): void {
    this.disposed = true;
    gpuPrepare.fn = null;
    gpuPrepare.source = null;
    if (this.prepareTimer !== undefined) clearTimeout(this.prepareTimer);
    this.prepared.clear();
    document.fonts.removeEventListener('loadingdone', this.fontsReady);
    this.world.destroy();
    this.screen.destroy();
    for (const layer of this.layers.values()) {
      layer.canvas.destroy();
      layer.target.destroy(true);
    }
    this.layers.clear();
    this.bank.destroy();
    this.blit.mesh.destroy();
    this.still?.target.destroy(true);
    this.target.destroy(true);
    this.renderer.destroy();
  }
}
